import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { AuthProvider, useAuth } from './context/AuthContext';
import { usePushNotifications } from './hooks/usePushNotifications';
import { I18nProvider } from './i18n';

// ── Eagerly loaded — core paths users hit on first render ──────────────────
import Layout from './components/Layout';
import HomeLayout from './components/HomeLayout';
import AuthScreen from './screens/AuthScreen';
import FeedScreen from './screens/FeedScreen';
import MovementFeed from './screens/movement/MovementFeed';
import PrayerLayout from './components/PrayerLayout';
import PrayerCalendarScreen from './screens/PrayerCalendarScreen';
import PrayerDiscoverScreen from './screens/PrayerDiscoverScreen';
import PrayerRequestsScreen from './screens/PrayerRequestsScreen';
import JournalScreen from './screens/JournalScreen';
import ProfileScreen from './screens/ProfileScreen';
import UsernameSetupModal from './components/UsernameSetupModal';
import GettingStartedScreen from './screens/GettingStartedScreen';
import { HelmetProvider } from 'react-helmet-async';
import './index.css';

// ── Code-split routes — fetched only when first needed ─────────────────────
// Admin screens are gated by role; community / well / chat / growth screens
// are large self-contained features. Breaking them out cuts the initial
// bundle so first-paint on the feed isn't paying for code only some users
// or admins ever load.
const CommunitiesScreen        = lazy(() => import('./screens/CommunitiesScreen'));
const CommunityRedirect        = lazy(() => import('./components/CommunityRedirect'));
const CommunityLayout          = lazy(() => import('./components/CommunityLayout'));
const CommunityFeedScreen      = lazy(() => import('./screens/CommunityFeedScreen'));
const CommunityHomeScreen      = lazy(() => import('./screens/CommunityHomeScreen'));
const CommunityBibleScreen     = lazy(() => import('./screens/CommunityBibleScreen'));
const CommunityCalendarScreen  = lazy(() => import('./screens/CommunityCalendarScreen'));
const CommunityMembersScreen   = lazy(() => import('./screens/CommunityMembersScreen'));
const CommunitySubScreen       = lazy(() => import('./screens/CommunitySubScreen'));
const CommunityInvitesScreen   = lazy(() => import('./screens/CommunityInvitesScreen'));
const CommunityManageScreen    = lazy(() => import('./screens/CommunityManageScreen'));
const CommunityFeaturesScreen  = lazy(() => import('./screens/CommunityFeaturesScreen'));
const CommunityLeaveScreen     = lazy(() => import('./screens/CommunityLeaveScreen'));
const CommunityGrowthScreen    = lazy(() => import('./screens/CommunityGrowthScreen'));
const WellFeedScreen           = lazy(() => import('./screens/WellFeedScreen'));
const WellThreadScreen         = lazy(() => import('./screens/WellThreadScreen'));
const WellThreadAdminScreen    = lazy(() => import('./screens/WellThreadAdminScreen'));
const DailyRhythmScreen        = lazy(() => import('./screens/rhythm/DailyRhythmScreen'));
const DailyRhythmDayScreen     = lazy(() => import('./screens/rhythm/DailyRhythmDayScreen'));
const DailyRhythmAdminScreen   = lazy(() => import('./screens/rhythm/DailyRhythmAdminScreen'));
const DailyRhythmDayEditor     = lazy(() => import('./screens/rhythm/DailyRhythmDayEditor'));
const BibleStudyScreen         = lazy(() => import('./screens/BibleStudyScreen'));
const PrayNowScreen            = lazy(() => import('./screens/PrayNowScreen'));
const CoursesScreen            = lazy(() => import('./screens/courses/CoursesScreen'));
const ChatScreen               = lazy(() => import('./screens/ChatScreen'));
const MembersScreen            = lazy(() => import('./screens/MembersScreen'));
const UserProfileScreen        = lazy(() => import('./screens/UserProfileScreen'));
const ConnectionsScreen        = lazy(() => import('./screens/ConnectionsScreen'));
const NotificationsScreen      = lazy(() => import('./screens/NotificationsScreen'));
const NotificationSettingsScreen = lazy(() => import('./screens/NotificationSettingsScreen'));
const SettingsLayout           = lazy(() => import('./components/SettingsLayout'));
const LoginSettingsScreen      = lazy(() => import('./screens/settings/LoginSettingsScreen'));
const InvitesSettingsScreen    = lazy(() => import('./screens/settings/InvitesSettingsScreen'));
const AccountSettingsScreen    = lazy(() => import('./screens/settings/AccountSettingsScreen'));
const FeedbackSettingsScreen   = lazy(() => import('./screens/settings/FeedbackSettingsScreen'));
const GettingStartedSettingsScreen = lazy(() => import('./screens/settings/GettingStartedSettingsScreen'));
const PrivacySettingsScreen    = lazy(() => import('./screens/settings/PrivacySettingsScreen'));
const PrivacyScreen            = lazy(() => import('./screens/PrivacyScreen'));
const TermsScreen              = lazy(() => import('./screens/TermsScreen'));
const SupportScreen            = lazy(() => import('./screens/SupportScreen'));
const PlanPreviewScreen        = lazy(() => import('./screens/PlanPreviewScreen'));
const InviteLandingScreen      = lazy(() => import('./screens/InviteLandingScreen'));
const PublicTestimonyScreen    = lazy(() => import('./screens/PublicTestimonyScreen'));
const PublicBibleStudyScreen   = lazy(() => import('./screens/PublicBibleStudyScreen'));
const PublicMovementStoryScreen = lazy(() => import('./screens/PublicMovementStoryScreen'));
const StoryView                = lazy(() => import('./screens/story/StoryView'));
const MovementStoryView        = lazy(() => import('./screens/movement/MovementStoryView'));
const AccountDeletionScreen    = lazy(() => import('./screens/AccountDeletionScreen'));
const ChildSafetyScreen        = lazy(() => import('./screens/ChildSafetyScreen'));
const AboutScreen              = lazy(() => import('./screens/AboutScreen'));
const DemoScreen               = lazy(() => import('./screens/demo/DemoScreen'));
const ComingFromPrayDayScreen  = lazy(() => import('./screens/ComingFromPrayDayScreen'));
const DownloadScreen           = lazy(() => import('./screens/DownloadScreen'));
const FAQScreen                = lazy(() => import('./screens/FAQScreen'));

// Admin bundle — only loaded for admins, never paid for by regular users.
const AdminLayout              = lazy(() => import('./components/AdminLayout'));
const AdminHomeScreen          = lazy(() => import('./screens/admin/AdminHomeScreen'));
const AdminDashboardScreen     = lazy(() => import('./screens/admin/AdminDashboardScreen'));
const AdminUserActivityScreen  = lazy(() => import('./screens/admin/AdminUserActivityScreen'));
const AdminMetricsScreen       = lazy(() => import('./screens/admin/AdminMetricsScreen'));
const AdminUsersScreen         = lazy(() => import('./screens/admin/AdminUsersScreen'));
const AdminCommunitiesScreen   = lazy(() => import('./screens/admin/AdminCommunitiesScreen'));
const AdminPrayerEventsScreen  = lazy(() => import('./screens/admin/AdminPrayerEventsScreen'));
const AdminPrayerRecipientsScreen = lazy(() => import('./screens/admin/AdminPrayerRecipientsScreen'));
const AdminPostsScreen         = lazy(() => import('./screens/admin/AdminPostsScreen'));
const AdminPrayNowsScreen      = lazy(() => import('./screens/admin/AdminPrayNowsScreen'));
const AdminBibleStudiesScreen  = lazy(() => import('./screens/admin/AdminBibleStudiesScreen'));
const AdminLogsScreen          = lazy(() => import('./screens/admin/AdminLogsScreen'));
const AdminAuditLogsScreen     = lazy(() => import('./screens/admin/AdminAuditLogsScreen'));
const AdminPrayerWallScreen    = lazy(() => import('./screens/admin/AdminPrayerWallScreen'));
const AdminGrowthScreen        = lazy(() => import('./screens/admin/AdminGrowthScreen'));
const AdminWellScreen          = lazy(() => import('./screens/admin/AdminWellScreen'));
const AdminNotifLogsScreen     = lazy(() => import('./screens/admin/AdminNotifLogsScreen'));
const AdminReportsScreen       = lazy(() => import('./screens/admin/AdminReportsScreen'));
const AdminFeedbackScreen      = lazy(() => import('./screens/admin/AdminFeedbackScreen'));
const AdminSystemPrayersScreen = lazy(() => import('./screens/admin/AdminSystemPrayersScreen'));
const AdminMovementStoriesScreen = lazy(() => import('./screens/admin/AdminMovementStoriesScreen'));
const AdminSocialPostsScreen   = lazy(() => import('./screens/admin/AdminSocialPostsScreen'));
const AdminIntegrationsScreen  = lazy(() => import('./screens/admin/AdminIntegrationsScreen'));
const AdminEmailsScreen        = lazy(() => import('./screens/admin/AdminEmailsScreen'));
const AdminEmailLogsScreen     = lazy(() => import('./screens/admin/AdminEmailLogsScreen'));
const AdminBlogScreen          = lazy(() => import('./screens/admin/AdminBlogScreen'));
const AdminNewsletterScreen    = lazy(() => import('./screens/admin/AdminNewsletterScreen'));
const AdminSettingsScreen      = lazy(() => import('./screens/admin/AdminSettingsScreen'));
const AdminAppAccessScreen     = lazy(() => import('./screens/admin/AdminAppAccessScreen'));
const BlogIndexScreen          = lazy(() => import('./screens/blog/BlogIndexScreen'));
const BlogPostScreen           = lazy(() => import('./screens/blog/BlogPostScreen'));
const UnsubscribeScreen        = lazy(() => import('./screens/blog/UnsubscribeScreen'));
const PeopleLayout             = lazy(() => import('./components/PeopleLayout'));
const PeopleScreen             = lazy(() => import('./screens/people/PeopleScreen'));
const EncountersTab            = lazy(() => import('./screens/people/EncountersTab'));
const PersonDetailScreen       = lazy(() => import('./screens/people/PersonDetailScreen'));
const EncounterPlanScreen      = lazy(() => import('./screens/people/EncounterPlanScreen'));
const LiveGuideScreen          = lazy(() => import('./screens/people/LiveGuideScreen'));
const DebriefScreen            = lazy(() => import('./screens/people/DebriefScreen'));
const GrowLayout               = lazy(() => import('./components/GrowLayout'));
const LineageScreen            = lazy(() => import('./screens/LineageScreen'));
const LineagePendingScreen     = lazy(() => import('./screens/LineagePendingScreen'));
const RelationshipDetailScreen = lazy(() => import('./screens/RelationshipDetailScreen'));

// Settings/connection content components are exports of lazy-loaded modules.
// We resolve them inside small wrapper components below so Suspense can
// surround them without exposing the lazy import to consumers.
const NotificationSettingsContentLazy = lazy(() =>
  import('./screens/NotificationSettingsScreen').then(m => ({ default: m.NotificationSettingsContent })));
const ConnectionsContentLazy = lazy(() =>
  import('./screens/ConnectionsScreen').then(m => ({ default: m.ConnectionsContent })));
const FAQContentLazy = lazy(() =>
  import('./screens/FAQScreen').then(m => ({ default: m.FAQContent })));

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading, needsUsername, needsOnboarding } = useAuth();

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', flexDirection:'column', gap:16 }}>
      <img src="/logo192.png" alt="Ignite" style={{width:48,height:48,borderRadius:12}}/>
      <span className="spinner" style={{ width:32, height:32 }}/>
    </div>
  );

  if (!isAuthenticated) return <Navigate to="/auth" replace />;

  // Social login users must set a username before entering the app
  if (needsUsername) return <UsernameSetupModal />;

  // New users see the getting started wizard before anything else
  if (needsOnboarding) return <GettingStartedScreen />;

  return children;
}

function AdminRoute({ children }) {
  const { isAdmin, loading } = useAuth();
  if (loading) return null;
  if (!isAdmin) return <Navigate to="/" replace />;
  return children;
}

// Ensures the push notification hook initialises on native even before
// PushPrompt (which is suppressed on native) would otherwise call it.
function NativePushRegistrar() {
  usePushNotifications();
  return null;
}

function AppRoutes() {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();

  // Handle NAVIGATE messages from the service worker (web push notification clicks)
  useEffect(() => {
    if (!navigator.serviceWorker) return;
    function handleMessage(e) {
      if (e.data?.type === 'NAVIGATE' && e.data?.url) {
        try {
          const url = new URL(e.data.url);
          navigate(url.pathname + url.search + url.hash);
        } catch {
          navigate(e.data.url);
        }
      }
    }
    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => navigator.serviceWorker.removeEventListener('message', handleMessage);
  }, [navigate]);

  // Handle ignite-navigate events from Capacitor push notification taps
  useEffect(() => {
    function handleNativeNav(e) {
      const url = e.detail?.url;
      if (!url) return;
      try {
        const parsed = new URL(url, window.location.origin);
        navigate(parsed.pathname + parsed.search + parsed.hash);
      } catch {
        navigate(url);
      }
    }
    window.addEventListener('ignite-navigate', handleNativeNav);
    return () => window.removeEventListener('ignite-navigate', handleNativeNav);
  }, [navigate]);

  // Handle deep links: HTTPS Universal Links / App Links to theigniteapp.com
  // and the com.theigniteapp.app:// custom scheme. OAuth callback URLs are
  // intentionally ignored here — AuthContext owns those.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let listener = null;
    let cancelled = false;
    import('@capacitor/app').then(({ App: CapApp }) => {
      if (cancelled) return null;
      return CapApp.addListener('appUrlOpen', ({ url }) => {
        if (!url) return;
        if (url.includes('code=') || url.includes('error=')) return;

        const CUSTOM_SCHEME = 'com.theigniteapp.app://';
        let path = null;
        if (url.startsWith(CUSTOM_SCHEME)) {
          const rest = url.slice(CUSTOM_SCHEME.length);
          path = rest.startsWith('/') ? rest : '/' + rest;
        } else {
          try {
            const parsed = new URL(url);
            const host = parsed.hostname;
            if (host !== 'theigniteapp.com' && host !== 'www.theigniteapp.com') return;
            path = parsed.pathname + parsed.search + parsed.hash;
          } catch {
            return;
          }
        }
        if (path) navigate(path);
      });
    }).then(l => {
      if (!l) return;
      if (cancelled) { l.remove?.(); return; }
      listener = l;
    });
    return () => {
      cancelled = true;
      listener?.remove?.();
    };
  }, [navigate]);

  // Full-screen spinner — used while Amplify is mid-OAuth exchange
  const Spinner = () => (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', flexDirection:'column', gap:16 }}>
      <img src="/logo192.png" alt="Ignite" style={{width:48,height:48,borderRadius:12}}/>
      <span className="spinner" style={{ width:32, height:32 }}/>
    </div>
  );

  // Inline fallback for any lazy-loaded screen — sized to fill the page area
  // without disturbing layout chrome. Less jarring than the full-screen Spinner.
  const RouteFallback = () => (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:'48px 0' }}>
      <span className="spinner" style={{ width:28, height:28 }}/>
    </div>
  );

  return (
    <>
      <NativePushRegistrar />
      <Suspense fallback={<RouteFallback />}>
      <Routes>
      {/* Show spinner on /auth while loading so we don't flash the login form
          mid-OAuth and then immediately redirect away once tokens arrive */}
      <Route path="/about"              element={<AboutScreen />} />
      <Route path="/demo"               element={<DemoScreen />} />
      <Route path="/coming-from-prayday" element={<ComingFromPrayDayScreen />} />
      {/* Public "get the app" page. /dl is the short one that goes on printed
          cards and QR codes; the longer spellings are aliases so a guessed URL
          never 404s. Deliberately OUTSIDE ProtectedRoute — almost everyone who
          needs it has no account yet. */}
      <Route path="/dl"                 element={<DownloadScreen />} />
      <Route path="/download"           element={<Navigate to="/dl" replace />} />
      <Route path="/get-the-app"        element={<Navigate to="/dl" replace />} />
      <Route path="/privacy"           element={<PrivacyScreen />} />
      <Route path="/terms"             element={<TermsScreen />} />
      <Route path="/support"           element={<SupportScreen />} />
      <Route path="/account-deletion"  element={<AccountDeletionScreen />} />
      <Route path="/child-safety"      element={<ChildSafetyScreen />} />
      <Route path="/plan/:planId"      element={<PlanPreviewScreen />} />
      <Route path="/invite/:token"     element={<InviteLandingScreen />} />
      <Route path="/t/:token"          element={<PublicTestimonyScreen />} />
      <Route path="/bs/:token"         element={<PublicBibleStudyScreen />} />
      <Route path="/m/:id"             element={<PublicMovementStoryScreen />} />
      <Route path="/m/:id/:slug"       element={<PublicMovementStoryScreen />} />
      <Route path="/blog"                  element={<BlogIndexScreen />} />
      <Route path="/blog/:slug"            element={<BlogPostScreen />} />
      <Route path="/newsletter/unsubscribe" element={<UnsubscribeScreen />} />
      <Route path="/auth" element={
        loading        ? <Spinner /> :
        isAuthenticated ? <Navigate to="/" replace /> :
        <AuthScreen />
      } />
      <Route path="/*" element={
        <ProtectedRoute>
          <Layout>
            <Routes>
              {/* ── Home: Feed · Stories · Journal ──────────────────────
                  A pathless layout route, so the three URLs stay exactly
                  where they already are — no redirects, and none of the
                  prefix-matching fragility that path="/" as a parent would
                  bring (it is a prefix of every other route). */}
              <Route element={<HomeLayout />}>
                <Route path="/"        element={<FeedScreen />} />
                <Route path="/stories" element={<MovementFeed />} />
                <Route path="/journal" element={<JournalScreen />} />
              </Route>
              <Route path="/privacy"            element={<PrivacyScreen />} />
              <Route path="/terms"              element={<TermsScreen />} />
              <Route path="/support"            element={<SupportScreen />} />
              <Route path="/groups"             element={<CommunitiesScreen />} />
              <Route path="/communities"        element={<CommunityRedirect />} />
              <Route path="/communities/explore" element={<CommunitiesScreen defaultTab="discover" />} />
              <Route path="/communities/:id" element={<CommunityLayout />}>
                {/* Home · Feed. The feed used to be the bottom half of Home;
                    it is its own tab so neither screen waits on the other's
                    requests. /activity was the pre-split URL. */}
                <Route index element={<CommunityHomeScreen />} />
                <Route path="feed"      element={<CommunityFeedScreen />} />
                <Route path="activity"  element={<Navigate to="../feed" replace />} />
                <Route path="calendar"  element={<CommunityCalendarScreen />} />
                <Route path="bible"     element={<CommunityBibleScreen />} />
                <Route path="growth"    element={<CommunityGrowthScreen />} />
                <Route path="members"   element={<CommunityMembersScreen />} />
                <Route path="sub"       element={<CommunitySubScreen />} />
                <Route path="invites"   element={<CommunityInvitesScreen />} />
                <Route path="manage"    element={<CommunityManageScreen />} />
                <Route path="features"  element={<CommunityFeaturesScreen />} />
                <Route path="leave"     element={<CommunityLeaveScreen />} />
                <Route path="brp/:planId"          element={<CommunityBibleScreen />} />
                <Route path="brp/:planId/:unitNum" element={<CommunityBibleScreen />} />
                <Route path="well"                 element={<WellFeedScreen />} />
                <Route path="well/new"             element={<WellThreadAdminScreen />} />
                <Route path="well/:threadId/edit"  element={<WellThreadAdminScreen />} />
                {/* Daily Rhythm. Nested under the community so CommunityLayout
                    supplies the hero and outlet context; nothing needs adding to
                    Layout.js since every path stays under /communities. */}
                <Route path="rhythm"                             element={<DailyRhythmScreen />} />
                <Route path="rhythm/manage"                      element={<DailyRhythmAdminScreen />} />
                <Route path="rhythm/manage/:programId"           element={<DailyRhythmAdminScreen />} />
                <Route path="rhythm/manage/:programId/day/:date" element={<DailyRhythmDayEditor />} />
                <Route path="rhythm/:programId/day/:date"        element={<DailyRhythmDayScreen />} />
              </Route>
              <Route path="/well/thread/:threadId" element={<WellThreadScreen />} />
              {/* ── Prayer: Calendar · Requests · Discover ─────────────── */}
              <Route path="/prayer" element={<PrayerLayout />}>
                <Route path="calendar"   element={<PrayerCalendarScreen />} />
                <Route path="discover"   element={<PrayerDiscoverScreen />} />
                <Route path="requests"   element={<PrayerRequestsScreen />} />
                {/* Recipients moved to People. Nested so the old URL redirects
                    instead of falling through to the catch-all below. */}
                <Route path="recipients" element={<Navigate to="/people" replace />} />
              </Route>
              <Route path="/prayer-wall"       element={<Navigate to="/prayer/requests" replace />} />

              {/* ── People: People · Encounters · Lineage ──────────────── */}
              <Route path="/people" element={<PeopleLayout />}>
                <Route index             element={<PeopleScreen />} />
                <Route path="encounters" element={<EncountersTab />} />
                <Route path="lineage"    element={<LineageScreen />} />
              </Route>
              {/* Drill-downs are full-screen detail views with their own back
                  buttons, not extra tabs — PeopleLayout reads its active tab
                  from the last path segment, and SectionTabs caps at three.
                  `/people/person/:id` rather than `/people/:id` so the segment
                  can never collide with a future tab name. */}
              <Route path="/people/person/:recipientId"      element={<PersonDetailScreen />} />
              <Route path="/people/person/:recipientId/plan" element={<EncounterPlanScreen />} />
              <Route path="/encounters/:id/guide"            element={<LiveGuideScreen />} />
              <Route path="/encounters/:id/debrief"          element={<DebriefScreen />} />
              <Route path="/lineage/pending"   element={<LineagePendingScreen />} />
              <Route path="/lineage/:relationshipId" element={<RelationshipDetailScreen />} />

              <Route path="/testimony/:id"     element={<StoryView />} />
              <Route path="/movement/:id"      element={<MovementStoryView />} />
              <Route path="/pray-now"          element={<PrayNowScreen />} />

              {/* ── Grow: My Plans · Explore · Bible Study ─────────────────
                  Pathless again, so plan and unit detail pages below sit
                  outside the tab strip. React Router ranks the static
                  `explore` / `bible-study` segments above `:planId`. */}
              <Route element={<GrowLayout />}>
                <Route path="/grow"             element={<CoursesScreen />} />
                <Route path="/grow/explore"     element={<CoursesScreen />} />
                <Route path="/grow/bible-study" element={<BibleStudyScreen />} />
              </Route>
              <Route path="/grow/:planId"          element={<CoursesScreen />} />
              <Route path="/grow/:planId/:unitNum" element={<CoursesScreen />} />

              {/* ── Redirects from the pre-restructure navigation ──────────
                  Must stay above the "*" catch-all, which sends anything
                  unmatched to Home and would otherwise swallow these.
                  Push payloads, emails and universal links all resolve
                  through the router, so these cover every entry point. */}
              <Route path="/courses"           element={<Navigate to="/grow" replace />} />
              <Route path="/bible-study"       element={<Navigate to="/grow/bible-study" replace />} />
              <Route path="/lineage"           element={<Navigate to="/people/lineage" replace />} />
              <Route path="/chat"              element={<ChatScreen />} />
              <Route path="/messages"         element={<ChatScreen />} />
              <Route path="/profile"           element={<ProfileScreen />} />
              <Route path="/members"           element={<MembersScreen />} />
              <Route path="/members/:id"       element={<UserProfileScreen />} />
              <Route path="/connections"       element={<ConnectionsScreen />} />
              <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
                <Route index element={<AdminHomeScreen />} />
                <Route path="dashboard" element={<AdminDashboardScreen />} />
                <Route path="user-activity" element={<AdminUserActivityScreen />} />
                <Route path="metrics" element={<AdminMetricsScreen />} />
                <Route path="users" element={<AdminUsersScreen />} />
                <Route path="communities" element={<AdminCommunitiesScreen />} />
                <Route path="prayer-events" element={<AdminPrayerEventsScreen />} />
                <Route path="prayer-recipients" element={<AdminPrayerRecipientsScreen />} />
                <Route path="posts" element={<AdminPostsScreen />} />
                <Route path="pray-nows" element={<AdminPrayNowsScreen />} />
                <Route path="bible-studies" element={<AdminBibleStudiesScreen />} />
                <Route path="logs" element={<AdminLogsScreen />} />
                <Route path="audit-logs" element={<AdminAuditLogsScreen />} />
                <Route path="prayer-wall" element={<AdminPrayerWallScreen />} />
                <Route path="growth" element={<AdminGrowthScreen />} />
                <Route path="well" element={<AdminWellScreen />} />
                <Route path="notif-logs" element={<AdminNotifLogsScreen />} />
                <Route path="reports" element={<AdminReportsScreen />} />
                <Route path="feedback" element={<AdminFeedbackScreen />} />
                <Route path="system-prayers" element={<AdminSystemPrayersScreen />} />
                <Route path="movement-stories" element={<AdminMovementStoriesScreen />} />
                <Route path="social-posts" element={<AdminSocialPostsScreen />} />
                <Route path="integrations" element={<AdminIntegrationsScreen />} />
                <Route path="emails" element={<AdminEmailsScreen />} />
                <Route path="email-logs" element={<AdminEmailLogsScreen />} />
                <Route path="blog"                          element={<AdminBlogScreen />} />
                <Route path="blog/:id"                      element={<AdminBlogScreen />} />
                <Route path="newsletter"                    element={<AdminNewsletterScreen />} />
                <Route path="newsletter/issues/:id"         element={<AdminNewsletterScreen />} />
                <Route path="settings"                      element={<AdminSettingsScreen />} />
                <Route path="app-access"                    element={<AdminAppAccessScreen />} />
              </Route>
              <Route path="/notifications"     element={<NotificationsScreen />} />
              <Route path="/notification-settings" element={<NotificationSettingsScreen />} />
              <Route path="/settings" element={<SettingsLayout />}>
                <Route index element={null} />
                <Route path="login" element={<LoginSettingsScreen />} />
                <Route path="notifications" element={<NotificationSettingsContentLazy />} />
                <Route path="connections" element={<ConnectionsContentLazy />} />
                <Route path="invites" element={<InvitesSettingsScreen />} />
                <Route path="faq" element={<FAQContentLazy />} />
                <Route path="feedback" element={<FeedbackSettingsScreen />} />
                <Route path="privacy" element={<PrivacySettingsScreen />} />
                <Route path="getting-started" element={<GettingStartedSettingsScreen />} />
                <Route path="account" element={<AccountSettingsScreen />} />
              </Route>
              <Route path="/getting-started"   element={<GettingStartedScreen />} />
              <Route path="/faq"                 element={<FAQScreen />} />
              <Route path="*"                  element={<Navigate to="/" replace />} />
            </Routes>
          </Layout>
        </ProtectedRoute>
      } />
    </Routes>
    </Suspense>
    </>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <I18nProvider>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </I18nProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}
